import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from 'store';
import { organizationActions } from 'actions/organizationActions';
import 'translations/translations';
import { Country, getCountries } from 'helpers/countries';
import OrganizationGroupCodeSelect from '../OrganizationGroupCodeSelect';

type StepProps = {
  stepTitleString: string;
  buttonText: string;
};

const useStyles = makeStyles((theme) => ({
  noMargine: {
    marginTop: 0,
  },
  flagStyles: {
    width: '2em',
    height: '1rem',
    marginRight: '0.5em',
    position: 'relative',
    top: '0.15rem',
  },
  error: {
    color: theme.palette.error.main,
    textAlign: 'right',
    fontSize: '0.8rem',
  },
}));

const CreateOrganizationStep: React.FC<StepProps> = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['translations']);
  const [organizationName, setOrganizationName] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const countries = getCountries(t);
  const [selectedCountry, setSelectedCountry] = useState(
    countries[0].alpha2Code,
  );
  const organizationError = useSelector(
    (state: ApplicationState) => state.organization?.error,
  );

  const classes = useStyles();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setOrganizationName(value);
    if (value.length >= 2) {
      dispatch({ type: 'ORGANIZATION_REMOVE_ERROR' });
      dispatch({ type: 'DISABLE_BUTTON', disabled: false });
    }
  };

  const handleChangeGroupCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setGroupCode(value);
  };

  const handleSelect = (value: string) => {
    setSelectedCountry(value);
  };

  const isGlobalAdmin = useSelector(
    (state: ApplicationState) =>
      state.isLoggedIn.userAccess?.isGlobalAdmin ?? false,
  );

  const callbackFn = () => {
    dispatch(
      organizationActions.createOrganization(
        organizationName,
        selectedCountry,
        groupCode,
      ),
    );
  };

  useEffect(() => {
    if (organizationName.length >= 2) {
      dispatch({ type: 'SET_CALLBACK', callbackFn });
    }
  });

  useEffect(() => {
    dispatch({ type: 'ORGANIZATION_STATE_RESET' });
  }, []);

  useEffect(() => {
    if (organizationName.length < 2) {
      dispatch({ type: 'DISABLE_BUTTON', disabled: true });
    }
  }, [organizationName, dispatch]);

  return (
    <>
      <Grid item xs={6}>
        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          id="organizationName"
          label={t(
            'organizations_page.create_org.create_org_step.organization_name',
          )}
          name="organizationName"
          autoFocus
          onChange={handleChange}
          className={classes.noMargine}
          required
        ></TextField>
        <Typography className={classes.error}>
          {!!organizationError && organizationError}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <OrganizationGroupCodeSelect
          onChange={handleChangeGroupCode}
          disabled={!isGlobalAdmin}
          helpText={
            !isGlobalAdmin
              ? t(
                  'organization_page.organization_information.labels.group_code_super_admin_only',
                )
              : ''
          }
        />
      </Grid>
      <Grid item xs={6}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel>{t('labels.select_country_dd')}</InputLabel>
          <Select
            value={selectedCountry}
            onChange={(
              e: React.ChangeEvent<{
                name?: string | undefined;
                value: unknown;
              }>,
            ) => handleSelect(e.target.value as string)}
            label={t('labels.select_country_dd')}
            id="countryDD"
          >
            {countries?.map((country: Country, index) => {
              return (
                <MenuItem value={country.alpha2Code} key={index}>
                  <img
                    className={classes.flagStyles}
                    alt={country.name}
                    src={country.flag}
                  />
                  {country.name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>
    </>
  );
};

export { CreateOrganizationStep };
